/**
 * Modal showing a single frame's image alongside Claude's full structured
 * analysis (scene, people, objects, actions, text, etc).
 */
import React from 'react';
import { frameImageUrl } from '../api/api';

function ListField({ label, items }) {
  if (!items || !items.length) return null;
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      <ul className="mt-1 flex flex-wrap gap-1.5">
        {items.map((item, idx) => (
          <li key={idx} className="text-xs bg-gray-100 text-gray-700 rounded-full px-2 py-0.5">
            {typeof item === 'string' ? item : item.description}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function FrameModal({ jobId, frame, onClose }) {
  if (!frame) return null;
  const a = frame.analysis || {};

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto scrollbar-thin"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-semibold">Frame at {frame.timestamp}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">
            &times;
          </button>
        </div>
        <img
          src={frameImageUrl(jobId, frame.fileName)}
          alt={`Frame at ${frame.timestamp}`}
          className="w-full max-h-80 object-contain bg-black"
        />
        <div className="p-5 space-y-4">
          {a.summary && <p className="text-gray-800 font-medium">{a.summary}</p>}
          {a.scene && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Scene</p>
              <p className="text-sm text-gray-700">{a.scene}</p>
            </div>
          )}
          <ListField label="People" items={a.people} />
          <ListField label="Objects" items={a.objects} />
          <ListField label="Actions" items={a.actions} />
          <ListField label="Facial Expressions" items={a.facial_expressions} />
          <ListField label="Clothing" items={a.clothing} />
          <ListField label="Vehicles" items={a.vehicles} />
          <ListField label="Animals" items={a.animals} />
          <ListField label="Visible Text (OCR)" items={a.text} />
          <ListField label="Logos" items={a.logos} />
          <ListField label="Screens" items={a.screens} />
          <ListField label="Notable Observations" items={a.notable_observations} />

          <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-gray-100">
            {a.environment && (
              <div>
                <p className="text-gray-400 text-xs">Environment</p>
                <p>{a.environment}</p>
              </div>
            )}
            {a.camera_angle && (
              <div>
                <p className="text-gray-400 text-xs">Camera Angle</p>
                <p>{a.camera_angle}</p>
              </div>
            )}
            {a.lighting && (
              <div>
                <p className="text-gray-400 text-xs">Lighting</p>
                <p>{a.lighting}</p>
              </div>
            )}
            {a.weather && (
              <div>
                <p className="text-gray-400 text-xs">Weather</p>
                <p>{a.weather}</p>
              </div>
            )}
            {a.time_of_day && (
              <div>
                <p className="text-gray-400 text-xs">Time of Day</p>
                <p>{a.time_of_day}</p>
              </div>
            )}
            {typeof a.confidence_score === 'number' && (
              <div>
                <p className="text-gray-400 text-xs">Confidence</p>
                <p>{Math.round(a.confidence_score * 100)}%</p>
              </div>
            )}
          </div>

          {a.error && <p className="text-red-600 text-sm">{a.error}</p>}
        </div>
      </div>
    </div>
  );
}
